import React, { useCallback, useState } from 'react';
import { Box, Typography, Button, AppBar, Toolbar, IconButton, Menu, MenuItem, Divider, useMediaQuery, useTheme } from '@mui/material';
import { Business, AccountCircle, ExitToApp, Person, ArrowBack } from '@mui/icons-material';
import { useClientContext } from '../context/ClientContextProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'shared-lib';
import { logo } from 'shared-lib';

interface PageHeaderProps {
  title?: string;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title }) => {
  const { currentProperty } = useClientContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleClose();
  };

  const handleOpenDrawer = useCallback(() => {
    // Event to be caught by parent component (App.tsx)
    const event = new CustomEvent('openPropertyDrawer');
    window.dispatchEvent(event);
  }, []);
  
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Get page title based on current path if not explicitly provided
  const getPageTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    if (path === '/') return 'Dashboard';
    if (path === '/register') return 'Register Property';
    if (path.includes('/requests/new')) return 'New Request';
    if (path.includes('/requests') && !path.includes('/requests/new')) {
      if (path.split('/').length > 4) return 'Request';
      return 'My Requests';
    }
    return 'Rent Ready Bids';
  };
  
  // Determine if back button should be shown
  const shouldShowBackButton = () => {
    const path = location.pathname;
    // Show back button on request details pages and register page
    return (path.includes('/requests/') && !path.includes('/requests/new')) || path === '/register';
  };

  // Don't show header on auth pages
  if (location.pathname === '/login') {
    return null;
  }

  return (
    <AppBar 
      position="sticky"
      color="transparent" 
      elevation={0}
      sx={{ 
        top: 0,
        backgroundColor: '#fff',
        borderBottom: '1px solid rgba(16, 29, 59, 0.1)',
        mb: isMobile ? 1 : 2,
        zIndex: 1100,
        borderRadius: 0,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0
      }}
    >
      <Toolbar 
        sx={{ 
          justifyContent: 'space-between', 
          px: isMobile ? 2 : 2,
          minHeight: isMobile ? 56 : 64, // Smaller toolbar height on mobile
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {shouldShowBackButton() && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleBack}
              sx={{ 
                mr: isMobile ? 0.5 : 1,
                color: '#101D3B',
                padding: isMobile ? '6px' : '8px',
              }}
            >
              <ArrowBack fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
            <img 
              src={logo} 
              alt="Rent Ready Bids" 
              style={{ 
                height: isMobile ? '20px' : '24px', 
                marginRight: isMobile ? '4px' : '8px'
              }} 
            />
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              sx={{ 
                fontWeight: isMobile ? 600 : 700,
                color: '#101D3B',
                fontSize: isMobile ? '16px' : '18px'
              }}
            >
              {getPageTitle()}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1 }}>
          {currentProperty && (
            <Button
            variant="contained"
            size="small"
            startIcon={<Business />}
            onClick={handleOpenDrawer}
            sx={{ 
              fontWeight: 500,
              backgroundColor: '#00AB9B',
              color: 'white',
              '&:hover': {
                backgroundColor: '#00AB9B',
                filter: 'brightness(0.9)'
              },
              padding: isMobile ? '4px 2px 4px 12px' : '6px 18px',
              fontSize: isMobile ? '12px' : '14px',
              minWidth: '10px',
            }}
          >
            {!isMobile && currentProperty.companyName}
          </Button> 
          )}

          <IconButton 
            color="inherit"
            onClick={handleProfileClick}
            aria-controls={open ? 'profile-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            sx={{ 
              color: 'rgba(16, 29, 59, 0.6)',
              '&:hover': {
                backgroundColor: 'rgba(16, 29, 59, 0.04)'
              },
              padding: isMobile ? '6px' : '8px',
            }}
          >
            <AccountCircle fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'profile-button',
            }}
            PaperProps={{
              sx: {
                minWidth: '220px',
                borderRadius: '10px',
                boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)',
                border: '1px solid rgba(16, 29, 59, 0.1)',
                mt: 1
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#101D3B' }}>
                Account
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(16, 29, 59, 0.6)' }}>
                {currentUser?.email || currentUser?.phoneNumber}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 1, borderColor: 'rgba(16, 29, 59, 0.1)' }} />
            
            {/* <MenuItem 
              onClick={handleClose}
              sx={{ 
                py: 1, 
                color: '#101D3B',
                '&:hover': {
                  backgroundColor: 'rgba(0, 171, 155, 0.04)'
                }
              }}
            >
              <Person sx={{ mr: 1, fontSize: '20px', color: 'rgba(16, 29, 59, 0.6)' }} />
              <Typography variant="body2">Profile Settings</Typography>
            </MenuItem> */}
            
            <MenuItem 
              onClick={handleLogout}
              sx={{ 
                py: 1, 
                color: '#d32f2f',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              <ExitToApp sx={{ mr: 1, fontSize: '20px' }} />
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default PageHeader; 